import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LaunchIcon from '@mui/icons-material/Launch';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useRef, useState } from 'react';
import {
  useDownloadClientDocumentMutation,
  useGetClientDocumentsQuery,
  useUploadClientDocumentMutation,
} from 'services/api/clients.api';
import type { Document } from 'services/types/document';
import type { User } from 'services/types/user';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOWEST,
} from 'styles/tokens';
import { BCI_REFI_ROBOT_BUSINESS_UNIT_ID } from 'utils/constants';

import InfoItem from './InfoItem';

interface DocumentsSectionProps {
  clientId: number;
  currentUser?: User | null;
  businessUnitId?: number | null;
}

/**
 * Helper function to create InfoItem components
 * @param label - The label for the info item
 * @param value - The value to display
 * @param fullWidth - Whether to take full width
 * @returns InfoItem component
 */
const createInfoItem = (label: string, value: React.ReactNode, fullWidth = false) => (
  <InfoItem key={`${label}-${Math.random()}`} label={label} value={value} fullWidth={fullWidth} />
);

/**
 * Extracts filename from Google Cloud Storage URL
 * @param url - The full URL string
 * @returns Extracted filename or the original URL if extraction fails
 */
const extractFilenameFromUrl = (url: string): string => {
  try {
    // Regex to match Google Cloud Storage URLs and extract the filename
    // Pattern: https://storage.googleapis.com/bucket-name/path/to/filename.ext?query-params
    const gcsPattern = /https:\/\/storage\.googleapis\.com\/[^/]+\/.*\/([^/?]+)(?:\?|$)/;
    const match = url.match(gcsPattern);

    if (match?.[1]) {
      return decodeURIComponent(match[1]);
    }

    // Fallback: try to extract filename from any URL
    const urlObj = new URL(url);
    const pathSegments = urlObj.pathname.split('/');
    const filename = pathSegments[pathSegments.length - 1];

    return filename || url;
  } catch {
    // If URL parsing fails, return the original URL
    return url;
  }
};

/**
 * Helper function to render URL as hyperlink for admin users
 * @param url - URL string to render
 * @param isAdmin - Whether current user is admin
 * @returns React node with hyperlink or null
 */
const renderUrlForAdmin = (url: string | null | undefined, isAdmin: boolean): React.ReactNode => {
  if (!url || !isAdmin) return null;

  const displayText = extractFilenameFromUrl(url);

  return (
    <Tooltip title={url} arrow placement="top">
      <Link
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          wordBreak: 'break-word',
          color: 'primary.main',
          textDecoration: 'underline',
          '&:hover': {
            textDecoration: 'none',
            color: 'primary.dark',
          },
        }}
      >
        {displayText}
      </Link>
    </Tooltip>
  );
};

/**
 * Gets a user-friendly document type label
 * @param type - Document type string
 * @returns Formatted document type label
 */
const getDocumentTypeLabel = (type: string | null): string => {
  if (!type) return 'Documento';

  const typeLabels: Record<string, string> = {
    identity_card: 'Cédula de Identidad',
    loan: 'Crédito',
    insurance_policy: 'Póliza de Seguro',
    bank_statement: 'Cartola Bancaria',
    debt_settlement_report: 'Reporte de Liquidación de Deudas',
    salary_settlement_report: 'Reporte de Liquidación de Salario',
    cmf_debt_report: 'Reporte de Deudas CMF',
    afp_contribution_certificate: 'Certificado de Cotizaciones AFP',
    utility_bill: 'Boleta de Servicio',
    additional_document: 'Documento Adicional',
    car_insurance_quotes: 'Cotizaciones de Seguro',
    other: 'Otro',
  };

  return typeLabels[type] || type.charAt(0).toUpperCase() + type.slice(1);
};

/**
 * Gets the appropriate color for document type chip
 * @param type - Document type string
 * @returns MUI color for the chip
 */
const getDocumentTypeColor = (
  type: string | null
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
  if (!type) return 'default';

  const colorMap: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error'> = {
    identity_card: 'primary',
    loan: 'primary',
    insurance_policy: 'primary',
    bank_statement: 'primary',
    debt_settlement_report: 'primary',
    salary_settlement_report: 'primary',
    cmf_debt_report: 'primary',
    afp_contribution_certificate: 'primary',
    utility_bill: 'primary',
    additional_document: 'default',
    other: 'default',
  };

  return colorMap[type] || 'default';
};

/**
 * Comprehensive documents section component for displaying client documents
 * @param clientId - Client ID for document operations
 * @param clientDocuments - Array of client documents
 * @param currentUser - Current user data for admin-only features
 * @returns React component displaying documents information
 */
const DocumentsSection: React.FC<DocumentsSectionProps> = ({ clientId, currentUser = null, businessUnitId = null }) => {
  // BCI Refi documents are sensitive: only global admins may preview or download them.
  const restrictDocuments = businessUnitId === BCI_REFI_ROBOT_BUSINESS_UNIT_ID && !currentUser?.isAdmin;
  const [downloadDocument] = useDownloadClientDocumentMutation();
  const [uploadDocument, { isLoading: isUploading }] = useUploadClientDocumentMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentPassword, setDocumentPassword] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { data: clientDocuments, isLoading } = useGetClientDocumentsQuery(String(clientId), {
    skip: !clientId,
  });

  const openUploadDialog = () => {
    setSelectedFile(null);
    setDocumentPassword('');
    setUploadError(null);
    setDialogOpen(true);
  };

  const closeUploadDialog = () => {
    if (isUploading) return;
    setDialogOpen(false);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    // Reset the input so selecting the same file again re-triggers the change event.
    event.target.value = '';
    setSelectedFile(file);
    setUploadError(null);
  };

  /**
   * Uploads the selected file (with an optional password) and triggers the AI reading.
   * A wrong password for a protected PDF surfaces the backend's precise error.
   */
  const handleUploadSubmit = async () => {
    if (!selectedFile) return;
    setUploadError(null);
    try {
      await uploadDocument({
        clientId,
        file: selectedFile,
        password: documentPassword.trim() || undefined,
      }).unwrap();
      setDialogOpen(false);
    } catch (error) {
      console.error('Error uploading document:', error);
      const message =
        (error as { data?: { error?: string } })?.data?.error ?? 'No se pudo subir el documento. Intenta nuevamente.';
      setUploadError(message);
    }
  };

  const coinButtonSx = {
    borderRadius: RADIUS_COIN,
    textTransform: 'none',
    fontWeight: 500,
    backgroundColor: 'yellow.main',
    color: NAVY,
    '&:hover': { backgroundColor: 'yellow.main', filter: 'brightness(0.97)' },
    '&.Mui-disabled': { backgroundColor: alpha(NAVY, 0.08), color: alpha(NAVY, 0.4) },
  } as const;

  const renderUploadControl = () => (
    <>
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          disableElevation
          startIcon={<UploadFileIcon fontSize="small" />}
          onClick={openUploadDialog}
          sx={coinButtonSx}
        >
          Subir documento
        </Button>
      </Box>
      <Dialog
        open={dialogOpen}
        onClose={closeUploadDialog}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              backdropFilter: GLASS_BLUR,
              backgroundColor: GLASS_BACKGROUND,
              boxShadow: SHADOW,
              borderRadius: RADIUS_CARD,
            },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: NAVY }}>Subir documento</DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            hidden
            onChange={handleFileInputChange}
          />
          <Button
            variant="text"
            startIcon={<UploadFileIcon fontSize="small" />}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              borderRadius: RADIUS_COIN,
              textTransform: 'none',
              color: NAVY,
              backgroundColor: alpha(NAVY, 0.06),
              '&:hover': { backgroundColor: alpha(NAVY, 0.12) },
            }}
          >
            {selectedFile ? 'Cambiar archivo' : 'Seleccionar archivo'}
          </Button>
          {selectedFile && (
            <Typography variant="body2" sx={{ mt: 1, color: alpha(NAVY, 0.7), wordBreak: 'break-all' }}>
              {selectedFile.name}
            </Typography>
          )}
          <TextField
            label="Contraseña del documento (opcional)"
            type="password"
            value={documentPassword}
            onChange={(event) => setDocumentPassword(event.target.value)}
            fullWidth
            size="small"
            margin="normal"
            helperText="Solo si el documento (PDF) está protegido con contraseña."
          />
          {uploadError && (
            <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 1 }}>
              {uploadError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeUploadDialog}
            disabled={isUploading}
            sx={{ textTransform: 'none', color: alpha(NAVY, 0.6) }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={!selectedFile || isUploading}
            startIcon={isUploading ? <CircularProgress size={16} sx={{ color: NAVY }} /> : undefined}
            onClick={() => {
              handleUploadSubmit().catch((error) => {
                console.error('Failed to upload document:', error);
              });
            }}
            sx={coinButtonSx}
          >
            {isUploading ? 'Procesando…' : 'Subir'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!clientDocuments || clientDocuments.length === 0) {
    return (
      <Box>
        {renderUploadControl()}
        <Typography
          sx={{
            color: 'text.secondary',
            fontStyle: 'italic',
          }}
        >
          No hay documentos disponibles para este cliente
        </Typography>
      </Box>
    );
  }

  /**
   * Handles document download
   * @param doc - Document to download
   */
  const handleDownload = async (doc: Document) => {
    try {
      const response = (await downloadDocument({
        clientId,
        documentId: doc.id,
      }).unwrap()) as Blob;

      // Create blob and download
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.aiName || `document_${doc.id}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  /**
   * Handles opening document in new tab
   * @param doc - Document to open
   */
  const handleOpenInNewTab = (doc: Document) => {
    const url = doc.url || doc.externalUrl;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      console.warn('No URL available for document:', doc.id);
    }
  };

  /**
   * Renders individual document details
   * @param document - Document data to render
   * @returns React component with document details
   */
  const renderDocumentDetails = (document: Document) => {
    const isAdmin = currentUser?.isAdmin === true;

    return (
      <Box>
        <Grid container spacing={0.5} sx={{ mb: 1 }}>
          {/* Basic document information */}
          {createInfoItem('ID', String(document.id))}
          {createInfoItem('Tipo', getDocumentTypeLabel(document.type))}
          {document.aiName && createInfoItem('Nombre', document.aiName)}
          {/* URL fields - only visible to admin users */}
          {renderUrlForAdmin(document.url, isAdmin) &&
            createInfoItem('URL', renderUrlForAdmin(document.url, isAdmin), true)}
          {renderUrlForAdmin(document.externalUrl, isAdmin) &&
            createInfoItem('URL Externa', renderUrlForAdmin(document.externalUrl, isAdmin), true)}
          {createInfoItem(
            'Fecha de creación',
            new Date(document.createdAt).toLocaleString('es-CL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })
          )}
          {createInfoItem(
            'Última actualización',
            new Date(document.updatedAt).toLocaleString('es-CL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })
          )}
        </Grid>
        {/* Extracted data from document */}
        {document.data && typeof document.data === 'object' && (
          <>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                mb: 1,
                color: 'text.secondary',
              }}
            >
              Información Extraída del Documento
            </Typography>
            <Grid container spacing={0.5} sx={{ mb: 1, ml: 1 }}>
              {Object.entries(document.data).map(([key, value]) => {
                if (value === null || value === undefined || value === '') return null;

                const formattedKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
                const formattedValue = typeof value === 'object' ? JSON.stringify(value) : String(value);

                return createInfoItem(formattedKey, formattedValue, formattedValue.length > 50);
              })}
            </Grid>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {renderUploadControl()}
      {/* Summary section */}
      <Box sx={{ mb: 2, p: 2, backgroundColor: SURFACE_CONTAINER_LOWEST, borderRadius: RADIUS_ITEM }}>
        <Typography
          variant="body2"
          sx={{
            color: alpha(NAVY, 0.5),
          }}
        >
          <strong>Total:</strong> {clientDocuments.length} documento{clientDocuments.length !== 1 ? 's' : ''}
        </Typography>
      </Box>
      {/* Individual document accordions */}
      <Box>
        {clientDocuments.map((document) => (
          <Accordion
            key={document.id}
            sx={{
              mb: 1,
              boxShadow: 'none',
              borderRadius: `${RADIUS_CARD} !important`,
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: '0 0 8px 0',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: alpha(NAVY, 0.55) }} />}
              sx={{
                backgroundColor: SURFACE_CONTAINER_LOWEST,
                borderRadius: `${RADIUS_CARD} ${RADIUS_CARD} 0 0`,
                minHeight: 56,
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  gap: 1.5,
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <DescriptionIcon color="primary" sx={{ flexShrink: 0 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
                  <Typography
                    variant="caption"
                    sx={{ color: alpha(NAVY, 0.5), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                  >
                    #{document.id}
                  </Typography>
                  <Chip
                    label={getDocumentTypeLabel(document.type)}
                    size="small"
                    color={getDocumentTypeColor(document.type)}
                    variant="filled"
                  />
                  {!restrictDocuments && (document.url || document.externalUrl) && (
                    <Tooltip title="Abrir en nueva pestaña" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenInNewTab(document);
                        }}
                        sx={{
                          color: 'primary.main',
                          '&:hover': { backgroundColor: 'primary.light', color: 'white' },
                        }}
                      >
                        <LaunchIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!restrictDocuments && (
                    <Tooltip title="Descargar documento" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(document).catch((error) => {
                            console.error('Failed to download document:', error);
                          });
                        }}
                        sx={{
                          color: 'primary.main',
                          '&:hover': { backgroundColor: 'primary.light', color: 'white' },
                        }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {new Date(document.createdAt).toLocaleDateString('es-CL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit',
                      })}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        fontSize: '0.7rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {new Date(document.createdAt).toLocaleTimeString('es-CL', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0.5, pb: 0.5, px: 1.5 }}>{renderDocumentDetails(document)}</AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default DocumentsSection;
